/**
 * Stock Chart Viewer - Finnhub API
 */

import { FINNHUB_API_KEY, FINNHUB_BASE, getCompanyName } from '../config.js';
import { getCachedData, setCachedData } from '../cache/storage.js';
import { incrementApiUsage } from '../cache/usage-tracker.js';

/**
 * Fetch stock data from Finnhub
 */
export async function fetchStockData(symbol, period = '1M', forceRefresh = false) {
    console.log('Finnhub Adapter v2 loaded (Force Daily)');
    // Check cache first
    const cached = getCachedData(symbol, period);
    if (!forceRefresh && cached) {
        return cached;
    }

    if (!FINNHUB_API_KEY) {
        throw new Error('Finnhub API Key is missing. Please check your configuration.');
    }

    // Determine resolution and time range based on period
    const { resolution, from, to } = getResolutionAndRange(period);

    try {
        console.log(`Fetching from Finnhub API: ${symbol} (${period})`);
        await incrementApiUsage();

        const url = `${FINNHUB_BASE}/stock/candle?symbol=${symbol}&resolution=${resolution}&from=${from}&to=${to}&token=${FINNHUB_API_KEY}`;

        const response = await fetch(url);
        const data = await response.json();

        if (data.s === 'no_data') {
            throw new Error('データが見つかりません。シンボルを確認してください。');
        }

        if (data.error) {
            throw new Error(`Finnhub API Error: ${data.error}`);
        }

        const parsedData = parseFinnhubData(data, symbol);

        // Enrich with Company Profile (Optional, separate call or mock)
        // For now, use local mapping or just symbol
        parsedData.company = getCompanyName(symbol);

        setCachedData(symbol, period, parsedData);
        return parsedData;

    } catch (error) {
        console.error('Finnhub fetch error:', error);
        if (cached) {
            console.log('API failed, using cached data');
            return cached;
        }
        throw new Error(error.message || 'データの取得に失敗しました。');
    }
}

/**
 * Helper to determine resolution and timestamps
 */
function getResolutionAndRange(period) {
    const now = Math.floor(Date.now() / 1000);
    let from;
    let resolution;

    // Finnhub resolutions: 1, 5, 15, 30, 60, D, W, M
    // Free Tier Restriction: Intraday (1-60) might be restricted or limited.
    // Safe bet for Free Tier is 'D' (Daily).
    switch (period) {
        case '1D':
            // Intraday '5' likely returns 403 on free tier. 
            // Reverting to 'D' for stability.
            resolution = 'D';
            from = now - (30 * 24 * 60 * 60); // Show 1 month context
            break;
        case '1W':
            resolution = 'D'; // Changed from '60' to avoid 403
            from = now - (30 * 24 * 60 * 60); // Show 1 month context
            break;
        case '1M':
            resolution = 'D'; // Daily
            from = now - (30 * 24 * 60 * 60);
            break;
        case '3M':
            resolution = 'D';
            from = now - (90 * 24 * 60 * 60);
            break;
        case '1Y':
            resolution = 'D';
            from = now - (365 * 24 * 60 * 60);
            break;
        case '5Y':
            resolution = 'W'; // Weekly
            from = now - (5 * 365 * 24 * 60 * 60);
            break;
        default:
            resolution = 'D';
            from = now - (30 * 24 * 60 * 60);
    }

    return { resolution, from, to: now };
}

/**
 * Parse Finnhub candle data to application format
 * Finnhub format: { c: [], h: [], l: [], o: [], t: [], v: [] }
 */
function parseFinnhubData(data, symbol) {
    if (!data.t || data.t.length === 0) {
        throw new Error('時系列データが空です。');
    }

    const candleData = [];
    const volumeData = [];

    data.t.forEach((timestamp, index) => {
        const time = new Date(timestamp * 1000).toISOString().split('T')[0]; // Simplify for now, or keep unix timestamp
        // Determine format based on resolution? App expects YYYY-MM-DD or Unix timestamp?
        // Checking alphavantage.js: "time = dateStr.split(' ')[0]" -> YYYY-MM-DD string
        // But for intraday, it might be different. Let's stick to YYYY-MM-DD for consistency
        // or check how chart/init.js handles it.
        // Actually, lightweight-charts supports string 'YYYY-MM-DD' or unix timestamp number.
        // Let's use unix timestamp for intraday and string for daily to match existing pattern if possible,
        // or just use unix timestamp (numbers) which is safer.
        // Let's use Unix Timestamp (in seconds) for logic consistency, 
        // BUT existing code seems to use string YYYY-MM-DD for daily.

        const open = data.o[index];
        const high = data.h[index];
        const low = data.l[index];
        const close = data.c[index];
        const volume = data.v[index];

        // Lightweight charts prefers seconds for unix timestamp
        const timeVal = timestamp;

        candleData.push({ time: timeVal, open, high, low, close });
        volumeData.push({
            time: timeVal,
            value: volume,
            color: close >= open ? 'rgba(38, 166, 65, 0.5)' : 'rgba(248, 81, 73, 0.5)',
        });
    });

    const latest = candleData[candleData.length - 1];
    const previous = candleData.length > 1 ? candleData[candleData.length - 2] : latest;

    const priceChange = latest.close - previous.close;
    const changePercent = (priceChange / previous.close) * 100;

    return {
        symbol: symbol.toUpperCase(),
        company: getCompanyName(symbol), // Would ideally fetch profile
        currentPrice: latest.close,
        change: priceChange,
        changePercent: changePercent,
        open: latest.open,
        high: Math.max(...candleData.map(c => c.high)),
        low: Math.min(...candleData.map(c => c.low)),
        volume: volumeData[volumeData.length - 1].value,
        candleData,
        volumeData,
    };
}

/**
 * Fetch Company News from Finnhub
 * @param {string} symbol
 * @returns {Promise<Array>}
 */
export async function fetchCompanyNews(symbol) {
    if (!FINNHUB_API_KEY) {
        console.warn('Finnhub API Key missing for news.');
        return [];
    }

    try {
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);

        const to = today.toISOString().split('T')[0];
        const from = sevenDaysAgo.toISOString().split('T')[0];

        const url = `${FINNHUB_BASE}/company-news?symbol=${symbol}&from=${from}&to=${to}&token=${FINNHUB_API_KEY}`;
        console.log(`Fetching News for ${symbol}...`);

        const response = await fetch(url);
        if (!response.ok) throw new Error('News fetch failed');

        const data = await response.json();
        return data.slice(0, 5); // Return top 5 recent news

    } catch (error) {
        console.error('Error fetching news:', error);
        return [];
    }
}

